import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  Bell, BellOff, AlertTriangle, AlertCircle, Info, CheckCircle, Clock,
  Loader, Filter, X, ChevronRight, Settings, Zap, Shield, TrendingUp,
  Database, Lightbulb, Calendar, BarChart3, ExternalLink, Check, FileWarning
} from 'lucide-react';

// Notification icons by category
const categoryIcons = {
  compliance_risk: Shield,
  regulatory_change: AlertTriangle,
  deadline_approaching: Calendar,
  deadline: Calendar,
  overdue_task: FileWarning,
  data_quality: Database,
  anomaly_detected: TrendingUp,
  recommendation: Lightbulb,
  agent_insight: Zap,
  system_alert: Info
};

// Severity colors
const severityColors = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-800',
    icon: 'text-red-500'
  },
  high: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-800',
    icon: 'text-amber-500'
  },
  medium: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-800',
    icon: 'text-blue-500'
  },
  low: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-700',
    badge: 'bg-slate-100 text-slate-800',
    icon: 'text-slate-500'
  },
  info: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    badge: 'bg-slate-100 text-slate-700',
    icon: 'text-slate-400'
  }
};

// Single Notification Card
const NotificationCard = ({ notification, onRead, onDismiss, onAction }) => {
  const colors = severityColors[notification.severity] || severityColors.info;
  const CategoryIcon = categoryIcons[notification.category] || Info;
  
  return (
    <div 
      className={`rounded-xl border-2 p-4 transition-all ${colors.bg} ${colors.border} ${
        !notification.read ? 'shadow-md' : 'opacity-75'
      }`}
      data-testid={`notification-${notification.notification_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${notification.read ? 'bg-white/50' : 'bg-white'}`}>
          <CategoryIcon size={20} className={colors.icon} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className={`font-bold ${colors.text} ${!notification.read ? '' : 'font-medium'}`}>
                {notification.title}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>
                  {notification.severity.toUpperCase()}
                </span>
                <span className="text-xs text-slate-500">
                  {notification.agent_source?.replace('_', ' ')}
                </span>
                <span className="text-xs text-slate-400">
                  {new Date(notification.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              {!notification.read && (
                <button
                  onClick={() => onRead(notification.notification_id)}
                  className="p-1 hover:bg-white/50 rounded transition-colors"
                  title="Mark as read"
                >
                  <Check size={16} className="text-slate-500" />
                </button>
              )}
              <button
                onClick={() => onDismiss(notification.notification_id)}
                className="p-1 hover:bg-white/50 rounded transition-colors"
                title="Dismiss"
              >
                <X size={16} className="text-slate-400" />
              </button>
            </div>
          </div>
          
          <p className={`text-sm mt-2 ${colors.text} opacity-80`}>
            {notification.message}
          </p>
          
          {notification.action_url && (
            <button
              onClick={() => onAction(notification.action_url)}
              className={`mt-3 text-sm font-medium flex items-center gap-1 ${colors.text} hover:underline`}
            >
              {notification.action_label || 'View Details'}
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Notification Summary Widget
const NotificationSummary = ({ summary }) => {
  if (!summary) return null;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
        <Bell size={24} className="mx-auto text-slate-600 mb-2" />
        <p className="text-2xl font-bold text-slate-900">{summary.total_unread}</p>
        <p className="text-xs text-slate-500">Unread</p>
      </div>
      <div className={`rounded-xl border p-4 text-center ${
        summary.has_critical ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
      }`}>
        <AlertCircle size={24} className={`mx-auto mb-2 ${summary.has_critical ? 'text-red-500' : 'text-slate-300'}`} />
        <p className={`text-2xl font-bold ${summary.has_critical ? 'text-red-600' : 'text-slate-400'}`}>
          {summary.by_severity?.critical || 0}
        </p>
        <p className="text-xs text-slate-500">Critical</p>
      </div>
      <div className={`rounded-xl border p-4 text-center ${
        summary.has_high ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
      }`}>
        <AlertTriangle size={24} className={`mx-auto mb-2 ${summary.has_high ? 'text-amber-500' : 'text-slate-300'}`} />
        <p className={`text-2xl font-bold ${summary.has_high ? 'text-amber-600' : 'text-slate-400'}`}>
          {summary.by_severity?.high || 0}
        </p>
        <p className="text-xs text-slate-500">High</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
        <Zap size={24} className="mx-auto text-emerald-500 mb-2" />
        <p className="text-2xl font-bold text-slate-900">
          {Object.keys(summary.by_agent || {}).length}
        </p>
        <p className="text-xs text-slate-500">Active Agents</p>
      </div>
    </div>
  );
};

// Main Notification Center Page
const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ severity: 'all', category: 'all', unreadOnly: false });
  const [showPreferences, setShowPreferences] = useState(false);

  const fetchData = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.unreadOnly) params.append('unread_only', 'true');
    if (filter.severity !== 'all') params.append('severity', filter.severity);
    if (filter.category !== 'all') params.append('category', filter.category);

    const [notificationsData, summaryData] = await Promise.all([
      apiFetch(`${API}/notifications/list?${params}`),
      apiFetch(`${API}/notifications/summary`)
    ]);

    if (notificationsData?._error || summaryData?._error) {
      console.error('Failed to fetch:', notificationsData?._error ? notificationsData.message : summaryData.message);
      setLoading(false);
      return;
    }

    setNotifications(notificationsData.notifications || []);
    setSummary(summaryData);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkAsRead = async (notificationId) => {
    const data = await apiFetch(`${API}/notifications/read/${notificationId}`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to mark as read:', data.message);
      return;
    }
    fetchData();
  };

  const handleMarkAllAsRead = async () => {
    const data = await apiFetch(`${API}/notifications/read-all`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to mark all as read:', data.message);
      return;
    }
    fetchData();
  };

  const handleDismiss = async (notificationId) => {
    const data = await apiFetch(`${API}/notifications/dismiss/${notificationId}`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to dismiss:', data.message);
      return;
    }
    fetchData();
  };

  const handleAction = (url) => {
    window.location.href = url;
  };

  const handleTriggerTest = async (type) => {
    const data = await apiFetch(`${API}/notifications/trigger-test`, { method: 'POST', body: { type } });
    if (data?._error) {
      console.error('Failed to trigger test:', data.message);
      return;
    }
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-center-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Proactive Notifications</h1>
            <p className="text-slate-300">
              AI-powered alerts for compliance risks, deadlines, and actionable insights
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
              data-testid="mark-all-read"
            >
              Mark All Read
            </button>
            <button
              onClick={() => setShowPreferences(!showPreferences)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Settings size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <NotificationSummary summary={summary} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Filters:</span>
        </div>
        
        <select
          value={filter.severity}
          onChange={(e) => setFilter(prev => ({ ...prev, severity: e.target.value }))}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        
        <select
          value={filter.category}
          onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All Categories</option>
          <option value="overdue_task">Overdue Tasks</option>
          <option value="compliance_risk">Compliance Risk</option>
          <option value="deadline_approaching">Deadlines</option>
          <option value="deadline">Deadline Alerts</option>
          <option value="anomaly_detected">Anomalies</option>
          <option value="recommendation">Recommendations</option>
          <option value="data_quality">Data Quality</option>
        </select>
        
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filter.unreadOnly}
            onChange={(e) => setFilter(prev => ({ ...prev, unreadOnly: e.target.checked }))}
            className="w-4 h-4 text-emerald-600 rounded"
          />
          <span className="text-sm text-slate-600">Unread only</span>
        </label>

        {/* Demo triggers */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-400">Demo:</span>
          <button
            onClick={() => handleTriggerTest('overdue')}
            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
            data-testid="trigger-overdue-btn"
          >
            + Overdue
          </button>
          <button
            onClick={() => handleTriggerTest('compliance_risk')}
            className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
          >
            + Risk
          </button>
          <button
            onClick={() => handleTriggerTest('deadline')}
            className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
          >
            + Deadline
          </button>
          <button
            onClick={() => handleTriggerTest('anomaly')}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            + Anomaly
          </button>
        </div>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <BellOff size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No Notifications</h3>
          <p className="text-sm text-slate-500">
            You're all caught up! AI agents will notify you when they detect important events.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.notification_id}
              notification={notification}
              onRead={handleMarkAsRead}
              onDismiss={handleDismiss}
              onAction={handleAction}
            />
          ))}
        </div>
      )}

      {/* Agent Sources */}
      {summary?.by_agent && Object.keys(summary.by_agent).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-4">Notifications by Agent</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.by_agent).map(([agent, count]) => (
              <div key={agent} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                <Zap size={16} className="text-emerald-500" />
                <span className="text-sm font-medium text-slate-700 capitalize">
                  {agent.replace('_', ' ')}
                </span>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
